<template>
  <AkCarouselV2
    :per-page="maxItemsPerPage"
    :per-move="perMove"
    :gap="effectiveItemSpacing"
    mode="slide"
    :draggable="isMobile"
    :arrows="arrowsConfig"
    :pagination="{ show: false }"
    :padding="{ left: 0, right: previewPercent, unit: '%' }"
  >
    <slot />
  </AkCarouselV2>
</template>

<script lang="ts">
import { defineComponent, inject, ref, PropType } from 'vue';
import { AkCarouselV2, type AkCarouselV2ArrowsConfig } from '@ankorstore/design-system';
import { CLIENT_DEVICE, withBreakpoints } from '@monolith/legacy/modules/design-system-candidates';
import {
  IS_QUICKVIEW_KEY,
  ITEM_PREVIEW_RATIO,
  ITEM_SPACING,
  ITEM_SPACING_MOBILE,
  MAX_ITEM_PREVIEW_RATIO,
  MAX_ITEMS,
  PER_MOVE,
} from './constants';
import { RecommendationAlgoType } from '@monolith/legacy/types/analytics/recommendation-tracking';

export default defineComponent({
  name: 'RecommendationsCarousel',
  components: { AkCarouselV2 },
  mixins: [withBreakpoints],
  props: {
    title: {
      type: String,
      default: null,
    },
    algorithm: {
      type: Number as PropType<RecommendationAlgoType>,
      default: null,
    },
    maxItemsOverride: {
      type: Number,
      default: undefined,
    },
    itemSpacingOverride: {
      type: Number,
      default: undefined,
    },
  },
  setup() {
    return {
      isQuickview: inject(IS_QUICKVIEW_KEY, ref(false)),
    };
  },
  computed: {
    maxItemsPerPage(): number {
      if (this.isQuickview) {
        return MAX_ITEMS.quickview[this.currentDevice];
      }
      if (this.maxItemsOverride !== undefined && this.currentDevice !== CLIENT_DEVICE.mobile) {
        return this.maxItemsOverride;
      }
      return this.algorithm === RecommendationAlgoType.productsCategories
        ? MAX_ITEMS.categories[this.currentDevice]
        : MAX_ITEMS.default[this.currentDevice];
    },
    perMove(): number {
      if (this.isQuickview) {
        return PER_MOVE.quickview[this.currentDevice];
      }
      return this.algorithm === RecommendationAlgoType.productsCategories
        ? PER_MOVE.categories[this.currentDevice]
        : PER_MOVE.default[this.currentDevice];
    },
    effectiveItemSpacing(): number {
      if (this.currentDevice === CLIENT_DEVICE.mobile) {
        return ITEM_SPACING_MOBILE;
      }
      return this.itemSpacingOverride ?? ITEM_SPACING;
    },
    previewPercent(): number {
      const ratio = Math.min(ITEM_PREVIEW_RATIO[this.currentDevice], MAX_ITEM_PREVIEW_RATIO);
      return Math.round((ratio / (this.maxItemsPerPage + ratio)) * 100);
    },
    arrowsConfig(): AkCarouselV2ArrowsConfig {
      return {
        display: this.isMobile ? 'none' : 'fixed',
        type: 'square',
        size: 'xl',
      };
    },
  },
});
</script>

<style scoped lang="scss">
// Give each direct slot child the correct slide width so AkCarouselV2 can
// navigate properly without requiring AkCarouselV2Slide wrappers in consumers.
:deep(.ak-carousel-v2-track__inner > *) {
  flex: 0 0 var(--slide-width, 100%);
  min-width: 0;
  overflow: hidden;
}
</style>
